import { useFinancialSummary } from "@babackapp/return-widget-components/hooks/use-financial-summary";
import { useSetRebuyRemainingCreditMethod } from "@babackapp/return-widget-components/hooks/use-set-rebuy-remaining-credit-method";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { formatPrice } from "@babackapp/return-widget-components/utils/format-price";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { loadCart } from "../lib/shop-now-cart";
import { useWidgetParams } from "./use-widget-params";

const remainingCreditSchema = z.object({
  method: z.enum(["refund", "discount"]),
});

type RemainingCreditFormValues = z.infer<typeof remainingCreditSchema>;

type UseShopNowRemainingCreditResult = {
  form: ReturnType<typeof useForm<RemainingCreditFormValues>>;
  forcedMethod: "refund" | "discount" | null;
  hasDiscountEnabled: boolean;
  formattedRemaining: string;
  handleConfirm: () => void;
};

export function useShopNowRemainingCredit(): UseShopNowRemainingCreditResult {
  const navigate = useNavigate();
  const { orderId, shopId } = useWidgetParams();
  const { data: order } = usePublicOrder(orderId);
  const { data: shop } = usePublicShop(shopId);
  const financialSummary = useFinancialSummary(orderId, order ?? null);
  const setMethod = useSetRebuyRemainingCreditMethod(orderId);
  const cart = loadCart(orderId);

  const cartTotal = cart ? cart.items.reduce((sum, i) => sum + i.price * i.quantity, 0) : 0;
  const remaining = financialSummary.exchangeTotal - cartTotal;

  const exchangeCreditRemainingMethod = (shop as any)?.exchange?.exchangeCreditRemainingMethod as string | undefined;
  const hasDiscountEnabled = !!(shop as any)?.discount?.available && !!(shop as any)?.discount?.activated;

  const forcedMethod: "refund" | "discount" | null = !hasDiscountEnabled
    ? "refund"
    : exchangeCreditRemainingMethod && exchangeCreditRemainingMethod !== "customer_choice"
    ? (exchangeCreditRemainingMethod as "refund" | "discount")
    : null;

  const form = useForm<RemainingCreditFormValues>({
    resolver: zodResolver(remainingCreditSchema),
    defaultValues: {
      method: forcedMethod ?? (hasDiscountEnabled ? "discount" : "refund"),
    },
  });

  const formattedRemaining = formatPrice(remaining, financialSummary.currency);

  const handleConfirm = form.handleSubmit((values) => {
    setMethod(values.method);
    navigate("../complete");
  });

  return { form, forcedMethod, hasDiscountEnabled, formattedRemaining, handleConfirm };
}
